import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from './Header';
import { formatDate, formatTime } from './utils/helpers';
import { format } from 'date-fns';
import './scss/Match.scss';
import Attendees from './components/Attendees';
import MatchCard from './components/match/MatchCard';
import TeamBuilder from './components/match/TeamBuilder';
import MotmVote from './components/match/MotmVote';
import MatchReport from './components/match/MatchReport';
import HeadToHead from './components/match/HeadToHead';
import api from './services/apiService';  
import { FaPencilAlt, FaFutbol, FaShareAlt, FaTrash, FaStar, FaChevronRight, FaMapMarkerAlt, FaCopy } from 'react-icons/fa';
import { authService } from './services/authService';
import GoogleMap from './utils/GoogleMap';
import { useNotification } from './components/NotificationProvider';

import shirt from './images/shirt.png'; // Black shirt
import PlayerShirt from './components/match/PlayerShirt';
import EditMatchModal from './components/match/EditMatchModal';
import ScoresModal from './components/match/ScoresModal';
import SocialPostModal from './components/match/SocialPostModal';

function Match() {
  const { matchId, tab } = useParams(); // Get matchId and tab from URL
  const [nextGameDate, setNextGameDate] = useState('');
  const [location, setLocation] = useState('');
  const [matchTime, setMatchTime] = useState('');
  const [response, setResponse] = useState(null);
  const [inviteId, setInviteId] = useState(null);
  const playerId = sessionStorage.getItem('player_id');
  const currentTeamName = sessionStorage.getItem('selected_team_name');
  const selectedTeamType = sessionStorage.getItem('selected_team_type');
  const navigate = useNavigate();
  const [teamColour, setTeamColour] = useState('');
  const [activeTab, setActiveTab] = useState(tab || 'details'); // Set initial tab based on URL
  const [attendance, setAttendance] = useState({ yes: [], no: [], awaiting: [] });
  const [matchResult, setMatchResult] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCoachOrManager, setIsCoachOrManager] = useState(false); // Add a specific state for coach/manager

  const [lastName, setLastName] = useState('');
  const [shirt_number, setShirtNumber] = useState('');
  const [maxPlayers, setMaxPlayers] = useState(null);
  const [totalYes, setTotalYes] = useState(0);
  const [teams, setTeams] = useState({ White: [], Colour: [] });
  const [matchDetails, setMatchDetails] = useState(null);
  const [scorers, setScorers] = useState(null);
  const [playerDetails, setPlayerDetails] = useState(null);
  const [players, setPlayers] = useState([]);
  const [allPlayers, setAllPlayers] = useState([]); // New state for combined players
  const [showEditModal, setShowEditModal] = useState(false);
  const [showScoresModal, setShowScoresModal] = useState(false);
  const [showSocialPostModal, setShowSocialPostModal] = useState(false);
  const [pitchDetails, setPitchDetails] = useState(null); // New state for pitch details
  const { showSuccess, showError } = useNotification();

  // Helper function to open address in maps
  const openInMaps = (address) => {
    const encodedAddress = encodeURIComponent(address);
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    
    if (isIOS) {
      // On iOS, try Apple Maps first (will open app if installed, otherwise web)
      // If user wants Google Maps, they can choose it from the options
      window.open(`https://maps.apple.com/?q=${encodedAddress}`, '_blank');
    } else {
      // On Android and desktop, use Google Maps
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank');
    }
  };

  // Helper function to copy address to clipboard with iOS Safari fallback
  const copyAddressToClipboard = async (address) => {
    try {
      // Try modern Clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(address);
        showSuccess('Address copied to clipboard!');
        return;
      }
      
      // Fallback for iOS Safari and older browsers
      const textArea = document.createElement('textarea');
      textArea.value = address;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        const successful = document.execCommand('copy');
        if (successful) {
          showSuccess('Address copied to clipboard!');
        } else {
          throw new Error('execCommand failed');
        }
      } catch (err) {
        throw new Error('Copy command failed');
      } finally {
        document.body.removeChild(textArea);
      }
    } catch (err) {
      console.error('Failed to copy address:', err);
      showError('Failed to copy address. Please try again.');
    }
  };

  // Add parent detection and child name for response section
  const isParent = authService.isParent();
  const childName = sessionStorage.getItem('child_name') || sessionStorage.getItem('first_name') || 'your child';
  const childFirstName = childName.split(' ')[0]; // Get just the first name

  useEffect(() => {
    // Check if the user is an admin
    const checkAdmin = () => {
      const admin = authService.isAdmin() || authService.isManager();
      setIsAdmin(admin);
      
      // Specifically check if user is a coach or manager (not player-manager)
      const userType = sessionStorage.getItem('user_type');
      const isManager = authService.isManager() && !playerId;
      const isCoach = userType === 'coach';
      setIsCoachOrManager(isManager || isCoach);
    };
    
    checkAdmin();

    // Ensure tabs scroll to the left when component mounts
    const resetTabsScroll = () => {
      const tabsContainer = document.querySelector('.tabs');
      if (tabsContainer) {
        tabsContainer.scrollLeft = 0;
      }
    };

    // Reset scroll position immediately and after a short delay to ensure DOM is ready
    resetTabsScroll();
    setTimeout(resetTabsScroll, 100);
    setTimeout(resetTabsScroll, 500);
    setTimeout(resetTabsScroll, 1000);

    setLastName(sessionStorage.getItem('last_name') || '');
    setShirtNumber(sessionStorage.getItem('shirt_number') || '');
    
    const fetchGameDetails = async () => {
      try {
        // Check if this is a dummy match
        if (matchId.includes('match-saturday-') || matchId.includes('training-')) {
          // Handle dummy match data
          const dummyMatchData = createDummyMatchData(matchId);
          setNextGameDate(formatDate(dummyMatchData.match_date));
          setLocation(dummyMatchData.location);
          setMatchTime(dummyMatchData.match_time);
          setResponse(dummyMatchData.response);
          setInviteId(null);
          setTeamColour('');
          setMaxPlayers(dummyMatchData.max_players);
          setTotalYes(dummyMatchData.total_yes);
          setMatchDetails(dummyMatchData);
          
          // Create fallback pitch details for dummy matches
          createFallbackPitchDetails(dummyMatchData.location);
          return;
        }

        const teamId = sessionStorage.getItem('selected_team_id');
        const isSuperAdminViewing = sessionStorage.getItem('superadmin_viewing_as_player') === 'true';
        const playerId = sessionStorage.getItem('player_id');
        const userType = sessionStorage.getItem('user_type');
        const isParent = userType === 'parent' || sessionStorage.getItem('is_parent') === 'true';
        
        // Build the API URL
        let apiUrl = `${process.env.REACT_APP_API_URL}/get_invite.php?match_id=${matchId}&team_id=${teamId}`;
        
        // If a superadmin is viewing as a player, add the player_id as a special parameter
        if (isSuperAdminViewing && authService.isSuperAdmin() && playerId) {
          apiUrl += `&superadmin_view_as_player_id=${playerId}`;
        }
        
        // If user is a parent, add the child_id parameter
        if (isParent && playerId) {
          apiUrl += `&child_id=${playerId}`;
        }
        
        const response = await api.get(apiUrl);
        const result = response.data;
        
        if (result.success && result.matches.length > 0) {
          const matchData = result.matches[0]; // Get the first (and only) match
          const matchDataWithId = {
            ...matchData,
            match_id: matchId  // Add match_id from URL params
          };
          
          // Log match details including team_size
          console.log('Match details loaded:', {
            match_id: matchId,
            team_size: matchData.team_size,
            team_type: matchData.team_type,
            max_players: matchData.max_players
          });
          
          setNextGameDate(formatDate(matchData.match_date));
          setLocation(matchData.location);
          setMatchTime(matchData.match_time);
          setResponse(matchData.response); // Set the player's current response
          setInviteId(matchData.invite_id);
          setTeamColour(matchData.colour || '');
          setMaxPlayers(matchData.max_players);
          setTotalYes(matchData.total_yes);
          setMatchDetails(matchDataWithId);
          
          // Process pitch details from the response
          if (matchData.pitch_id && matchData.pitch_name) {
            // Create pitchDetails object from the fields in the response
            const pitchData = {
              pitch_id: matchData.pitch_id,
              name: matchData.pitch_name,
              address_1: matchData.pitch_address_1 || '',
              address_2: matchData.pitch_address_2 || '',
              town: matchData.pitch_town || '',
              county: matchData.pitch_county || '',
              postcode: matchData.pitch_postcode || '',
              facilities: matchData.pitch_facilities || '',
              organiser_contact: matchData.pitch_contact || ''
            };
            
            setPitchDetails(pitchData);
          } else if (matchData.location) {
            // Fallback to using just the location field if no pitch data
            createFallbackPitchDetails(matchData.location);
          }
        } else {
          console.error('Failed to fetch game details:', {
            success: result.success,
            matchesLength: result.matches?.length,
            result
          });
        }
      } catch (error) {
        console.error('Error fetching game details:', error);
      }
    };

    const fetchAttendance = async () => {
      try {
        const teamId = sessionStorage.getItem('selected_team_id');
        const isSuperAdminViewing = sessionStorage.getItem('superadmin_viewing_as_player') === 'true';
        const playerId = sessionStorage.getItem('player_id');
        const userType = sessionStorage.getItem('user_type');
        const isParent = userType === 'parent' || sessionStorage.getItem('is_parent') === 'true';
        
        // Build the API URL
        let apiUrl = `${process.env.REACT_APP_API_URL}/get_attendance.php?match_id=${matchId}&team_id=${teamId}`;
        
        // If a superadmin is viewing as a player, add the player_id as a special parameter
        if (isSuperAdminViewing && authService.isSuperAdmin() && playerId) {
          apiUrl += `&superadmin_view_as_player_id=${playerId}`;
        }
        
        // If user is a parent, add the child_id parameter
        if (isParent && playerId) {
          apiUrl += `&child_id=${playerId}`;
        }
        
        const response = await api.get(apiUrl);
        const data = response.data;
        
        if (data.success) {
          // Set all players with their responses
          setPlayers([
            ...(data.data.yes || []),
            ...(data.data.no || []),
            ...(data.data.awaiting || [])
          ]);

          // Store the full attendance data including uninvited
          setAttendance(data.data); // Add this line

          // If you need a flat array of all players for other purposes
          setAllPlayers([
            ...(data.data.yes || []),
            ...(data.data.no || []),
            ...(data.data.awaiting || [])
          ]);
        }
      } catch (error) {
        console.error('Error fetching attendance:', error);
      }
    };

    const fetchMatchResult = async () => {
      try {
        const isSuperAdminViewing = sessionStorage.getItem('superadmin_viewing_as_player') === 'true';
        const playerId = sessionStorage.getItem('player_id');
        const userType = sessionStorage.getItem('user_type');
        const isParent = userType === 'parent' || sessionStorage.getItem('is_parent') === 'true';
        
        // Build the API URL
        let apiUrl = `${process.env.REACT_APP_API_URL}/get_match_result.php?match_id=${matchId}`;
        
        // If a superadmin is viewing as a player, add the player_id as a special parameter
        if (isSuperAdminViewing && authService.isSuperAdmin() && playerId) {
          apiUrl += `&superadmin_view_as_player_id=${playerId}`;
        }
        
        // If user is a parent, add the child_id parameter
        if (isParent && playerId) {
          apiUrl += `&child_id=${playerId}`;
        }
        
        const response = await api.get(apiUrl);
        const result = response.data;

        
        if (result.success) {
          // Try to get match details from various possible locations in the response
          const matchDetailsData = result.match || result.match_details || result.data || {};
          setMatchDetails(matchDetailsData);
          
          setMatchResult({
            ...result.data,
            // Include score data from the main result object
            score_white: result.match?.score_white || result.data?.score_white,
            score_colour: result.match?.score_colour || result.data?.score_colour,
            motm_names: result.data?.motm_names || null,
            own_goals: result.legacy_own_goals || result.own_goals || (result.match && result.match.legacy_own_goals) || (result.match && result.match.own_goals)
          });
          setScorers(result.legacy_scorers || result.scorers || (result.match && result.match.legacy_scorers) || (result.match && result.match.scorers));
          

        } else {
          console.error('Failed to fetch match result:', result.message);
        }
      } catch (error) {
        console.error('Error fetching match result:', error);
      }
    };

    const fetchTeams = async () => {
      try {
        const isSuperAdminViewing = sessionStorage.getItem('superadmin_viewing_as_player') === 'true';
        const playerId = sessionStorage.getItem('player_id');
        const userType = sessionStorage.getItem('user_type');
        const isParent = userType === 'parent' || sessionStorage.getItem('is_parent') === 'true';
        
        // Build the API URL
        let apiUrl = `${process.env.REACT_APP_API_URL}/get_teams.php?match_id=${matchId}`;
        
        // If a superadmin is viewing as a player, add the player_id as a special parameter
        if (isSuperAdminViewing && authService.isSuperAdmin() && playerId) {
          apiUrl += `&superadmin_view_as_player_id=${playerId}`;
        }
        
        // If user is a parent, add the child_id parameter
        if (isParent && playerId) {
          apiUrl += `&child_id=${playerId}`;
        }
        
        const response = await api.get(apiUrl);
        const result = response.data;
        if (result.success) {
          setTeams(result.teams);
        } else {
          console.error('Failed to fetch teams');
        }
      } catch (error) {
        console.error('Error fetching teams:', error);
      }
    };



    const fetchPlayerDetails = async () => {
      try {
        const isSuperAdminViewing = sessionStorage.getItem('superadmin_viewing_as_player') === 'true';
        const playerId = sessionStorage.getItem('player_id');
        
        // Build the API URL
        let apiUrl = `${process.env.REACT_APP_API_URL}/get_match_player_details.php?match_id=${matchId}`;
        
        // If a superadmin is viewing as a player, add the player_id as a special parameter
        if (isSuperAdminViewing && authService.isSuperAdmin() && playerId) {
          apiUrl += `&superadmin_view_as_player_id=${playerId}`;
        }
        
        const response = await api.get(apiUrl);
        const data = response.data;
        if (data.status === 'success') {
          setPlayerDetails(data.data);
        }
      } catch (error) {
        console.error('Error fetching player details:', error);
      }
    };

    const createDummyMatchData = (matchId) => {
      // Handle match_id: 1 as a past match with score
      if (matchId === '1') {
        // Get current date
        const today = new Date();
        
        // Calculate past Saturday date (1 week ago)
        const pastSaturdayDate = new Date(today);
        pastSaturdayDate.setDate(today.getDate() - 7);
        
        return {
          match_id: matchId,
          type: 'match',
          match_date: format(pastSaturdayDate, 'yyyy-MM-dd'),
          match_time: '14:00:00',
          location: 'Home Ground',
          max_players: 12,
          total_yes: 10,
          total_no: 2,
          total_awaiting: 0,
          response: 'Yes',
          entry_open: false,
          opposition: 'FC United',
          team_type: 'friendly',
          match_type: 'Friendly',
          isDummy: true,
          // Add score data for past match
          score_white: 3,
          score_colour: 2,
          played_match: true
        };
      } else if (matchId.includes('past-match-')) {
        // Handle past matches with scores
        const pastMatch = matchId.match(/past-match-(\d+)/);
        const week = parseInt(pastMatch[1]);
        
        // Get current date
        const today = new Date();
        
        // Calculate past Saturday date
        const pastSaturdayDate = new Date(today);
        pastSaturdayDate.setDate(today.getDate() - (week * 7));
        
        // Different opposition teams for variety
        const oppositionTeams = ['FC United', 'City Rovers', 'Athletic Club', 'United Stars'];
        const locations = ['Home Ground', 'Away Pitch', 'Community Stadium', 'Sports Complex'];
        
        // Add scores for past matches
        const scores = [
          { white: 3, colour: 2 },
          { white: 1, colour: 1 },
          { white: 4, colour: 0 },
          { white: 2, colour: 3 }
        ];
        
        return {
          match_id: matchId,
          type: 'match',
          match_date: format(pastSaturdayDate, 'yyyy-MM-dd'),
          match_time: '14:00:00',
          location: locations[(week - 1) % locations.length],
          max_players: 12,
          total_yes: 10,
          total_no: 2,
          total_awaiting: 0,
          response: 'Yes',
          entry_open: false,
          opposition: oppositionTeams[(week - 1) % oppositionTeams.length],
          team_type: 'friendly',
          match_type: 'Friendly',
          isDummy: true,
          // Add score data for past matches
          score_white: scores[(week - 1) % scores.length].white,
          score_colour: scores[(week - 1) % scores.length].colour,
          played_match: true
        };
      } else if (matchId.includes('match-saturday-')) {
        // Handle Saturday matches
        const weekMatch = matchId.match(/match-saturday-(\d+)/);
        const week = parseInt(weekMatch[1]);
        
        // Get current date
        const today = new Date();
        
        // Calculate Saturday date for the specified week
        const saturdayDate = new Date(today);
        saturdayDate.setDate(today.getDate() + (6 - today.getDay() + 7) % 7 + ((week - 1) * 7));
        
        // Different opposition teams for variety
        const oppositionTeams = ['FC United', 'City Rovers', 'Athletic Club', 'United Stars'];
        const locations = ['Home Ground', 'Away Pitch', 'Community Stadium', 'Sports Complex'];
        
        return {
          match_id: matchId,
          type: 'match',
          match_date: format(saturdayDate, 'yyyy-MM-dd'),
          match_time: '14:00:00',
          location: locations[(week - 1) % locations.length],
          max_players: 12,
          total_yes: 7,
          total_no: 2,
          total_awaiting: 3,
          response: 'Invited',
          entry_open: true,
          opposition: oppositionTeams[(week - 1) % oppositionTeams.length],
          team_type: 'friendly',
          match_type: 'Friendly',
          isDummy: true
        };
      } else if (matchId.includes('training-')) {
        // Handle training sessions
        const trainingMatch = matchId.match(/training-(tuesday|thursday)-(\d+)/);
        const day = trainingMatch[1];
        const week = parseInt(trainingMatch[2]);
        
        // Get current date
        const today = new Date();
        
        // Calculate training date for the specified week and day
        const dayIndex = day === 'tuesday' ? 2 : 4; // Tuesday = 2, Thursday = 4
        const trainingDate = new Date(today);
        trainingDate.setDate(today.getDate() + (dayIndex - today.getDay() + 7) % 7 + ((week - 1) * 7));
        
        return {
          match_id: matchId,
          type: 'training',
          match_date: format(trainingDate, 'yyyy-MM-dd'),
          match_time: '18:00:00',
          location: 'Claverham Community College',
          max_players: 12,
          total_yes: day === 'tuesday' ? 7 : 8,
          total_no: day === 'tuesday' ? 2 : 1,
          total_awaiting: day === 'tuesday' ? 3 : 2,
          response: 'Invited',
          entry_open: true,
          duration: 90,
          notes: `Regular ${day.charAt(0).toUpperCase() + day.slice(1)} training session`,
          isDummy: true
        };
      }
      
      // Fallback for unknown dummy match types
      return {
        match_id: matchId,
        type: 'match',
        match_date: format(new Date(), 'yyyy-MM-dd'),
        match_time: '14:00:00',
        location: 'Default Location',
        max_players: 12,
        total_yes: 0,
        total_no: 0,
        total_awaiting: 0,
        response: 'Invited',
        entry_open: true,
        isDummy: true
      };
    };

    // Create a fallback pitch details object with just the location
    const createFallbackPitchDetails = (locationText) => {
      setPitchDetails({
        name: locationText,
        address_1: locationText,
        town: '',
        county: '',
        postcode: '',
        // No facilities or organiser_contact in fallback
      });
    };

    fetchGameDetails();
    if (matchId) {
      fetchAttendance();
      fetchMatchResult();
      fetchTeams();
      fetchPlayerDetails();
    }
  }, [playerId, matchId]);

  // Separate useEffect to handle tabs scroll position only on initial load
  useEffect(() => {
    const handleTabsScroll = () => {
      const tabsContainer = document.querySelector('.tabs');
      if (tabsContainer) {
        tabsContainer.scrollLeft = 0;
        tabsContainer.classList.add('force-left');
        
        // Remove the class after a short delay
        setTimeout(() => {
          tabsContainer.classList.remove('force-left');
        }, 100);
      }
    };

    // Only handle on mount, not when activeTab changes
    handleTabsScroll();
    
    // Also handle after a delay to ensure everything is rendered
    const timer = setTimeout(handleTabsScroll, 200);
    
    // Handle orientation change
    const handleOrientationChange = () => {
      setTimeout(handleTabsScroll, 100);
    };
    
    window.addEventListener('orientationchange', handleOrientationChange);
    window.addEventListener('resize', handleOrientationChange);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('resize', handleOrientationChange);
    };
  }, []); // Remove activeTab dependency

  useEffect(() => {
    if (matchDetails) {
        const matchDateTime = new Date(
            `${matchDetails.match_date} ${matchDetails.match_time}`
        );
        const currentDate = new Date();

        console.log({
            matchDateTime: matchDateTime.toString(),
            currentDate: currentDate.toString(),
            matchDate: matchDetails.match_date,
            matchTime: matchDetails.match_time,
            isStarted: currentDate > matchDateTime
        });
    }
  }, [matchDetails]);

  const handleResponseChange = async (newResponse) => {
    // Validate inviteId before making the request
    if (!inviteId) {
      console.error('Missing inviteId - aborting update');
      showError('Unable to update response at this time. Please try refreshing the page.');
      return;
    }

    // Check if parent has permission to respond to matches for this child
    const playerId = sessionStorage.getItem('player_id');
    if (authService.isParent() && !authService.canRespondToMatches(playerId)) {
      showError('You do not have permission to respond to matches for this child.');
      return;
    }

    // Show confirmation dialog if changing from Yes to No
    if (response === 'Yes' && newResponse === 'No') {
      const confirmed = window.confirm('Are you sure? We will notify the coach.');
      if (!confirmed) {
        return;
      }
    }

    try {
      const formData = new FormData();
      formData.append('invite_id', inviteId);
      formData.append('response', newResponse);
      
      const apiResponse = await api.post(`${process.env.REACT_APP_API_URL}/update_invite.php`, formData);

      const result = apiResponse.data;

      if (result.success) {
        setResponse(newResponse);
        if (newResponse === 'Yes') {
          showSuccess('Thanks for confirming! We look forward to seeing you at the match.', 5000);
        } else {
          showSuccess('Response updated. We hope to see you at a future match.', 5000);
        }
      } else {
        console.error('Failed to update response:', result);
        showError('Failed to update response');
      }
    } catch (error) {
      console.error('Error updating response:', error);
      showError('An error occurred while updating your response');
    }
  };



  const isMatchFinished = () => {
    console.log("isMatchFinished called with matchDetails:", matchDetails);
    
    if (!matchDetails) {
        console.log("isMatchFinished: matchDetails is null/undefined");
        return false;
    }
    
    if (!matchDetails.match_date || !matchDetails.match_time) {
        console.log("isMatchFinished: missing match_date or match_time", {
            match_date: matchDetails.match_date,
            match_time: matchDetails.match_time
        });
        return false;
    }
    
    try {
        // Parse match date and time to create a Date object
        const [year, month, day] = matchDetails.match_date.split('-');
        const [hours, minutes, seconds] = matchDetails.match_time.split(':');
        
        // Validate that we have valid numbers
        if (!year || !month || !day || !hours || !minutes || !seconds) {
            console.log("isMatchFinished: invalid date/time format", {
                match_date: matchDetails.match_date,
                match_time: matchDetails.match_time,
                parsed: { year, month, day, hours, minutes, seconds }
            });
            return false;
        }
        
        // JavaScript months are 0-indexed (0-11), so subtract 1 from month
        const matchStartTime = new Date(parseInt(year), parseInt(month)-1, parseInt(day), parseInt(hours), parseInt(minutes), parseInt(seconds));
        
        // Check if the date is valid
        if (isNaN(matchStartTime.getTime())) {
            console.log("isMatchFinished: invalid date object created", {
                match_date: matchDetails.match_date,
                match_time: matchDetails.match_time,
                matchStartTime: matchStartTime
            });
            return false;
        }
        
        // Get duration in minutes (default to 60 if not specified)
        const duration = parseInt(matchDetails.duration) || 60;
        
        // Calculate end time by adding duration to start time
        const matchEndTime = new Date(matchStartTime.getTime());
        matchEndTime.setMinutes(matchEndTime.getMinutes() + duration);
        
        const now = new Date();
        const isFinished = now > matchEndTime;
        
        console.log("isMatchFinished calculation:", {
            match_date: matchDetails.match_date,
            match_time: matchDetails.match_time,
            matchStartTime: matchStartTime.toISOString(),
            duration,
            matchEndTime: matchEndTime.toISOString(),
            now: now.toISOString(),
            isFinished
        });
        
        return isFinished;
    } catch (error) {
        console.error("isMatchFinished error:", error, {
            match_date: matchDetails.match_date,
            match_time: matchDetails.match_time
        });
        return false;
    }
  };



  // NEW TAB SYNC EFFECT - Add this effect after isMatchFinished is defined to avoid reference errors
  useEffect(() => {
    // Redirect away from head-to-head tab if team type is friendly
    if (matchDetails?.team_type === 'friendly' && activeTab === 'head-to-head') {
      setActiveTab('details');
      navigate(`/match/${matchId}/details`);
      return;
    }
    
    // Handle URL tab parameter
    if (tab) {
      setActiveTab(tab);
    } 
    // Auto-redirect to result tab for finished matches
    else if (matchDetails && activeTab !== 'result') {
      // Calculate if match is finished manually to avoid circular reference issues
      if (matchDetails.match_date && matchDetails.match_time) {
        try {
          const [year, month, day] = matchDetails.match_date.split('-');
          const [hours, minutes, seconds] = matchDetails.match_time.split(':');
          
          // Validate that we have valid numbers
          if (year && month && day && hours && minutes && seconds) {
            // Create match start and end times
            const matchStartTime = new Date(parseInt(year), parseInt(month)-1, parseInt(day), parseInt(hours), parseInt(minutes), parseInt(seconds));
            
            // Check if the date is valid
            if (!isNaN(matchStartTime.getTime())) {
              const duration = parseInt(matchDetails.duration) || 60;
              const matchEndTime = new Date(matchStartTime.getTime());
              matchEndTime.setMinutes(matchEndTime.getMinutes() + duration);
              
              const isFinished = new Date() > matchEndTime;
              
              if (isFinished) {
                setActiveTab('result');
                navigate(`/match/${matchId}/result`);
              } else if (!tab) {
                // Default to details tab for future/current matches with no specified tab
                setActiveTab('details');
              }
            } else if (!tab) {
              // Default to details tab if date is invalid
              setActiveTab('details');
            }
          } else if (!tab) {
            // Default to details tab if date/time format is invalid
            setActiveTab('details');
          }
        } catch (error) {
          console.error("Tab redirection error:", error);
          if (!tab) {
            setActiveTab('details');
          }
        }
      } else if (!tab) {
        // Default to details tab if we don't have match date/time info
        setActiveTab('details');
      }
    }
  }, [tab, matchDetails, matchId, navigate, activeTab]);

  const handleTabChange = (newTab) => {
    // If trying to set the same tab that's already active, do nothing
    if (newTab === activeTab) return;
    
    // Update the local state
    setActiveTab(newTab);
    
    // Navigate to the new URL
    navigate(`/match/${matchId}/${newTab}`, { replace: false });
  };



  const renderTabContent = () => {
    switch (activeTab) {
      case 'details':
        return (
          <div className="match-details">
            {/* Date */}
            <div className="match-date">
              <span>{nextGameDate}</span>
            </div>

            {/* Game Type */}
            <div className="game-type">
              <span>
                {matchDetails?.team_type === 'friendly' ? 'Friendly' : (matchDetails?.match_type || '')}
              </span>
            </div>

            {/* Teams Section */}
            <div className="teams-section">
              {/* Left Team Column */}
              <div className="team-column home-team">
                <div className="team-shield">
                  {(() => {
                    // Load home team logo using API approach like MatchCard.js
                    const defaultLightLogo = require('./images/teams/logo-light.png');
                    let homeTeamLogo = defaultLightLogo;
                    
                    const isHome = matchDetails?.is_home === 1 || matchDetails?.is_home === '1';
                    
                    // For friendly teams, use team_a, otherwise use current team
                    if (matchDetails?.team_type === 'friendly' && matchDetails?.team_a) {
                      // For friendly teams, try to load team_a logo if we have a team_id
                      // This would need to be implemented based on how team_a maps to team_id
                      // For now, use default logo
                    } else {
                      // For academy/squad teams, use current team logo for home games, opposition for away games
                      if (isHome) {
                        const teamId = sessionStorage.getItem('selected_team_id');
                        if (teamId) {
                          homeTeamLogo = `https://football.pebbledesigns.co.uk/api/images/teams/${teamId}/logo-dark.png`;
                        }
                      } else {
                        // Away game - use opposition team logo
                        if (matchDetails?.opposition_team_id) {
                          homeTeamLogo = `https://football.pebbledesigns.co.uk/api/images/teams/${matchDetails.opposition_team_id}/logo-dark.png`;
                        }
                      }
                    }
                    
                    return (
                      <img 
                        className="team-shield-img" 
                        src={homeTeamLogo} 
                        alt={(() => {
                          const isHome = matchDetails?.is_home === 1 || matchDetails?.is_home === '1';
                          
                          if (matchDetails?.team_type === 'friendly') {
                            return matchDetails.team_a;
                          } else {
                            return isHome ? (currentTeamName || 'Home Team') : (matchDetails?.opposition || 'Away Team');
                          }
                        })()}
                        onError={(e) => {
                          e.target.src = defaultLightLogo;
                        }}
                      />
                    );
                  })()}
                </div>
                <p className="team-name">
                  {(() => {
                    const isHome = matchDetails?.is_home === 1 || matchDetails?.is_home === '1';
                    
                    if (matchDetails?.team_type === 'friendly') {
                      // For friendlies, always show team_a on left, team_b on right
                      return matchDetails.team_a;
                    } else {
                      // For academies/squad, swap based on home/away
                      return isHome ? (currentTeamName || 'MNF') : (matchDetails?.opposition || 'Opposition');
                    }
                  })()}
                </p>
              </div>

              {/* Start Time (Center) */}
              <div className="match-time-center">
                <span>{matchDetails?.status === 'TBC' ? 'TBC' : formatTime(matchTime, matchDetails?.status)}</span>
              </div>

              {/* Right Team Column */}
              <div className="team-column away-team">
                <div className="team-shield">
                  {(() => {
                    // Load away team logo using API approach like MatchCard.js
                    const defaultLightLogo = require('./images/teams/logo-light.png');
                    let awayTeamLogo = defaultLightLogo;
                    
                    const isHome = matchDetails?.is_home === 1 || matchDetails?.is_home === '1';
                    
                    // For friendly teams, use team_b, otherwise use opposition
                    if (matchDetails?.team_type === 'friendly' && matchDetails?.team_b) {
                      // For friendly teams, try to load team_b logo if we have a team_id
                      // This would need to be implemented based on how team_b maps to team_id
                      // For now, use default logo
                    } else {
                      // For academy/squad teams, use opposition team logo for home games, current team for away games
                      if (isHome) {
                        // Home game - use opposition team logo
                        if (matchDetails?.opposition_team_id) {
                          awayTeamLogo = `https://football.pebbledesigns.co.uk/api/images/teams/${matchDetails.opposition_team_id}/logo-dark.png`;
                        }
                      } else {
                        // Away game - use current team logo
                        const teamId = sessionStorage.getItem('selected_team_id');
                        if (teamId) {
                          awayTeamLogo = `https://football.pebbledesigns.co.uk/api/images/teams/${teamId}/logo-dark.png`;
                        }
                      }
                    }
                    
                    return (
                      <img 
                        className="team-shield-img" 
                        src={awayTeamLogo} 
                        alt={(() => {
                          const isHome = matchDetails?.is_home === 1 || matchDetails?.is_home === '1';
                          
                          if (matchDetails?.team_type === 'friendly') {
                            return matchDetails.team_b;
                          } else {
                            return isHome ? (matchDetails?.opposition || 'Away Team') : (currentTeamName || 'Home Team');
                          }
                        })()}
                        onError={(e) => {
                          e.target.src = defaultLightLogo;
                        }}
                      />
                    );
                  })()}
                </div>
                <p className="team-name">
                  {(() => {
                    const isHome = matchDetails?.is_home === 1 || matchDetails?.is_home === '1';
                    
                    if (matchDetails?.team_type === 'friendly') {
                      // For friendlies, always show team_a on left, team_b on right
                      return matchDetails.team_b;
                    } else {
                      // For academies/squad, swap based on home/away
                      return isHome ? (matchDetails?.opposition || 'Opposition') : (currentTeamName || 'MNF');
                    }
                  })()}
                </p>
              </div>
            </div>

            {/* Arrival Note */}
            <div className="arrival-note">
              <span>🕐 Please arrive {matchDetails?.arrival_time_minutes || 60} minutes before kick-off</span>
            </div>

            {/* Venue */}
            <div className="venue-info">
              <span>Venue: <span 
                className="venue-location clickable" 
                onClick={() => handleTabChange('location')}
                style={{ cursor: 'pointer', textDecoration: 'underline' }}
              >
                {pitchDetails?.name || location}
              </span></span>
            </div>

            {/* Display instructions if available */}
            {matchDetails?.instructions && matchDetails.instructions.trim() !== '' && (
              <div className="match-instructions">
                <div className="instructions-content">
                  <h4>Instructions</h4>
                  <p>{matchDetails.instructions}</p>
                </div>
              </div>
            )}

            {/* Show status message only for cancelled or postponed matches */}
            {matchDetails?.status && (matchDetails.status === 'cancelled' || matchDetails.status === 'postponed') && (
              <div className="match-status-message">
                <div className={`status-banner ${matchDetails.status}`}>
                  {matchDetails.status === 'cancelled' ? 'Cancelled' : 
                   matchDetails.status === 'postponed' ? 'Postponed' : ''}
                </div>
                {matchDetails.last_update_message && (
                  <p className="status-reason">
                    {matchDetails.last_update_message}
                  </p>
                )}
              </div>
            )}

            {selectedTeamType !== 'academy' && (
              <>
                {teamColour === 'White' ? (
                  <div className="player-shirt">
                    <PlayerShirt 
                      color="#fff"
                      shirtNumber={shirt_number}
                      lastName={lastName}
                    />
                  </div>
                ) : teamColour === 'Colour' ? (
                  <div className="player-shirt">
                    <PlayerShirt 
                      color="#9f1717"
                      shirtNumber={shirt_number}
                      lastName={lastName}
                    />
                  </div>
                ) : (
                  <div className="player-shirt">
                    <img src={shirt} alt="Team Shirt" style={{ maxWidth: '200px' }} />
                  </div>
                )}
              </>
            )}

            {/* Only show response buttons if match is active */}
            {(!matchDetails?.status || matchDetails.status === 'active') && (
              <>
                {/* Response Buttons - show for all but disable for children */}
                {!isCoachOrManager && (
                    <div className='response-section'>
                        {parseInt(totalYes) >= parseInt(maxPlayers) ? (
                            <p><em>⚠️ We have reached our maximum number of players</em></p>
                        ) : (
                            <>
                                {parseInt(totalYes) >= (parseInt(maxPlayers) * 0.8) && (
                                    <p><em>⚠️ Hurry! Last few places remaining ⚠️</em></p>
                                )}
                                <p>{isParent ? `Can ${childFirstName} play?` : 'Can you play?'}</p>
                                <div className='response-buttons'>
                                    <button 
                                      className={`response-btn yes ${response === 'Yes' ? 'active' : response === 'No' ? 'inactive' : ''} ${authService.isChild() ? 'disabled' : ''}`} 
                                      onClick={() => !authService.isChild() && handleResponseChange('Yes')}
                                      disabled={authService.isChild()}>Yes</button>
                                    <button 
                                      className={`response-btn no ${response === 'No' ? 'active' : response === 'Yes' ? 'inactive' : ''} ${authService.isChild() ? 'disabled' : ''}`} 
                                      onClick={() => !authService.isChild() && handleResponseChange('No')}
                                      disabled={authService.isChild()}>No</button>
                                </div>
                                {/* Show responder information for parents and children */}
                                <div className="responder-info">
                                    <small style={{ fontStyle: 'italic', color: '#666' }}>
                                        {response ? (
                                            <>
                                                {isParent ? (
                                                    <>
                                                        Responded by {matchDetails?.responder_first_name && matchDetails?.responder_last_name ? `${matchDetails.responder_first_name} ${matchDetails.responder_last_name}` : 'a parent'}
                                                        {matchDetails?.response_updated ? (
                                                            <> on {new Date(matchDetails.response_updated).toLocaleDateString()} at {new Date(matchDetails.response_updated).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</>
                                                        ) : (
                                                            <> on {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</>
                                                        )}
                                                    </>
                                                ) : (
                                                    <>
                                                        Responded on {matchDetails?.response_updated ? (
                                                            <>{new Date(matchDetails.response_updated).toLocaleDateString()} at {new Date(matchDetails.response_updated).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</>
                                                        ) : (
                                                            <>{new Date().toLocaleDateString()} at {new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</>
                                                        )}
                                                    </>
                                                )}
                                            </>
                                        ) : (
                                            <>Response needed</>
                                        )}
                                    </small>
                                </div>
                            </>
                        )}
                    </div>
                )}
              </>
            )}
            


          </div>
        );
      case 'attendance':
        return (
          <Attendees 
            attendance={{
              yes: players.filter(p => p.response === 'Yes'),
              no: players.filter(p => p.response === 'No'),
              awaiting: players.filter(p => !p.response || p.response === 'Awaiting'),
              uninvited: attendance.uninvited
            }}
            isAdmin={isAdmin}
            match_id={matchId}
            maxPlayers={maxPlayers}
            matchDetails={matchDetails}
            selectedTeamType={selectedTeamType}
          />
        );
      case 'result':
        return (
          <div className="match-result match-details">
            <h3 className="tab-title">Match Result</h3>
            <MatchCard
              match={{
                match_id: matchId || '0',
                match_date: matchDetails?.match_date || '2024-01-01',
                match_time: matchDetails?.match_time || '19:00:00',
                location: matchDetails?.location || 'Default Location',
                score_white: matchResult?.score_white || matchDetails?.score_white || '0',
                score_colour: matchResult?.score_colour || matchDetails?.score_colour || '0',
                played_match: matchResult?.played_match || matchDetails?.played_match || false,
                team_colour: matchDetails?.team_colour || teamColour || 'White',
                player_id: playerId || '00',
                team_name: matchDetails?.team_name || currentTeamName || 'MNF',
                motm_names: matchResult?.motm_names || null,
                motm: matchResult?.motm || null,
                motm_first_name: matchDetails?.motm_first_name || null,
                motm_last_name: matchDetails?.motm_last_name || null,
                motm_name: matchDetails?.motm_name || null,
                scorers: scorers || matchResult?.legacy_scorers || null,
                match_type: matchDetails?.match_type,
                team_a: matchDetails?.team_a,
                team_b: matchDetails?.team_b,
                opposition: matchDetails?.opposition,
                is_home: matchDetails?.is_home,
                own_goals: matchResult?.legacy_own_goals || matchResult?.own_goals || null,
                // Add team IDs for proper shield display
                team_a_id: matchDetails?.team_a_id || sessionStorage.getItem('selected_team_id'),
                team_b_id: matchDetails?.team_b_id,
                opposition_team_id: matchDetails?.opposition_team_id,
                // Mark this as a finished match to hide response totals
                isFinishedMatch: true
              }}
            />
                        {isAdmin && (matchResult?.played_match || matchDetails?.played_match) && matchDetails?.score_white !== null && matchDetails?.score_colour !== null && !matchDetails?.motm_first_name && (
              <button 
                className="view-lineup-button"
                onClick={() => setShowSocialPostModal(true)}
              >
                <FaShareAlt /> Create Social Post
              </button>
            )}
            {isMatchFinished() && !isCoachOrManager && (
              <button 
                className="view-lineup-button motm-button"
                onClick={() => handleTabChange('motm')}
              >
                Vote For Your POTM <FaChevronRight />
              </button>
            )}
          </div>
        );
      case 'builder':
        return <TeamBuilder teams={teams} isAdmin={isAdmin} match={matchDetails} playerDetails={playerDetails} />;
      case 'motm':
        return <MotmVoteWrapper isAdmin={isAdmin} />;
      case 'report':
        return <MatchReport match_id={matchId} isAdmin={isAdmin} />;
      case 'location':
        // Build the full address string
        const fullAddress = pitchDetails 
          ? `${pitchDetails.address_1}${pitchDetails.address_2 ? ', ' + pitchDetails.address_2 : ''}, ${pitchDetails.town}${pitchDetails.county ? ', ' + pitchDetails.county : ''}${pitchDetails.postcode ? ', ' + pitchDetails.postcode : ''}`
          : location;

        return (
          <div className="location-tab match-details">
            <h3 className="tab-title">Location Details</h3>
            <p className="location-name">Location: <span className="audiowide">{pitchDetails?.name || location}</span></p>
            
            {/* Full address as small text above map */}
            {pitchDetails && (
              <p className="full-address-text">
                {pitchDetails.address_1}
                {pitchDetails.address_2 && `, ${pitchDetails.address_2}`}
                {pitchDetails.town && `, ${pitchDetails.town}`}
                {pitchDetails.county && `, ${pitchDetails.county}`}
                {pitchDetails.postcode && `, ${pitchDetails.postcode}`}
              </p>
            )}
            
            {/* Action buttons */}
            {(location || pitchDetails) && fullAddress && (
              <div className="location-action-buttons">
                <button 
                  className="location-action-btn open-maps-btn"
                  onClick={() => openInMaps(fullAddress)}
                  aria-label="Open in maps"
                >
                  <FaMapMarkerAlt /> Open in Maps
                </button>
                <button 
                  className="location-action-btn copy-address-btn"
                  onClick={() => copyAddressToClipboard(fullAddress)}
                  aria-label="Copy address"
                >
                  <FaCopy /> Copy Address
                </button>
              </div>
            )}
            
            {(location || pitchDetails) && (
              <div className="match-location-map">
                <GoogleMap 
                  addresses={[
                    pitchDetails ? 
                    `${pitchDetails.address_1}, ${pitchDetails.address_2 || ''}, ${pitchDetails.town}, ${pitchDetails.county || ''}, ${pitchDetails.postcode}` 
                    : location
                  ]} 
                />
              </div>
            )}
          </div>
        );
      case 'head-to-head':
        return (
          <HeadToHead 
            matchDetails={matchDetails}
            currentTeamName={currentTeamName}
            isActive={activeTab === 'head-to-head'}
          />
        );
      default:
        return null;
    }
  };

  const MotmVoteWrapper = () => {
    const [playersWithPositions, setPlayersWithPositions] = React.useState([]);
    
    React.useEffect(() => {
      const loadPlayersWithPositions = async () => {
        try {
          const response = await api.get(`${process.env.REACT_APP_API_URL}/get_motm_team_players.php?match_id=${matchId}`);
          const result = response.data;
          
          if (!result.success || !Array.isArray(result.players)) {
            console.error('Failed to load players:', result);
            return;
          }
          
          const playersWithDetails = result.players.map(player => ({
            ...player,
            position: formatPosition(player.position)
          }));
          
          setPlayersWithPositions(playersWithDetails);
        } catch (error) {
          console.error('Error fetching player positions:', error);
        }
      };
      
      loadPlayersWithPositions();
    }, []);
    
    return <MotmVote match_id={matchId} players={playersWithPositions} isAdmin={isAdmin} />;
  };

  // Add helper function to format position
  const formatPosition = (position) => {
    if (!position) return 'N/A';
    
    const positionMap = {
      'forward-left': 'LW',
      'forward-right': 'RW',
      'forward-center': 'ST',
      'midfield-left': 'LM',
      'midfield-right': 'RM',
      'midfield-center': 'CM',
      'defense-left': 'LB',
      'defense-right': 'RB',
      'defense-center': 'CB'
    };
    
    return positionMap[position] || position;
  };

  const handleDeleteMatch = async () => {
    // Add confirmation dialog
    const isConfirmed = window.confirm(
      'Are you sure you want to delete this match? This action cannot be undone.'
    );
    
    if (!isConfirmed) {
      return; // User cancelled the operation
    }
    
    try {
      const response = await api.post(`${process.env.REACT_APP_API_URL}/delete_match.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          match_id: matchId
        })
      });
      
      if (response.data.success) {
        showSuccess('Match deleted successfully', 5000);
        navigate('/dashboard');
      } else {
        console.error('Failed to delete match:', response.data.message);
        showError('Failed to delete match: ' + (response.data.message || 'Unknown error'), 5000);
      }
    } catch (error) {
      console.error('Error deleting match:', error);
      showError('An error occurred while deleting the match', 5000);
    }
  };

  return (
    <div className="Match container">
      <Header />
      <div className="header-actions">
        <button className="back-button" onClick={() => navigate(-1)}>← Back</button>
        {isAdmin && (
          <>
            <button 
              className="edit-match-button"
              onClick={() => setShowEditModal(true)}
              title="Edit Match"
            >
              <FaPencilAlt />
            </button>
            <button 
              className="edit-scores-button"
              onClick={() => setShowScoresModal(true)}
              title="Update Scores"
            >
              <FaFutbol />
            </button>
            <button 
              className="delete-match-button"
              onClick={() => handleDeleteMatch()}
              title="Delete Match"
            >
              <FaTrash />
            </button>
          </>
        )}
      </div>
      
      {/* Show tabs if match is active, or if there are time/venue changes */}
      {(!matchDetails?.status || matchDetails.status === 'active' || matchDetails.status === 'time_changed' || matchDetails.status === 'venue_changed' || matchDetails.status === 'venue_time_changed') && (
        <div className="tabs">
          {!isMatchFinished() && (
            <button 
              className={activeTab === 'details' ? 'active' : ''} 
              onClick={() => handleTabChange('details')}
            >
              Details
            </button>
          )}
          {!isMatchFinished() && (
            <button 
              className={activeTab === 'attendance' ? 'active' : ''} 
              onClick={() => handleTabChange('attendance')}
            >
              Attendees
            </button>
          )}
          {isMatchFinished() && (
            <button 
              className={activeTab === 'result' ? 'active' : ''} 
              onClick={() => handleTabChange('result')}
            >
              Result
            </button>
          )}
          {isMatchFinished() && (
            <button 
              className={activeTab === 'motm' ? 'active' : ''} 
              onClick={() => handleTabChange('motm')}
            >
              POTM
            </button>
          )}
          {isMatchFinished() && (
            <button 
              className={activeTab === 'report' ? 'active' : ''} 
              onClick={() => handleTabChange('report')}
            >
              Report
            </button>
          )}
          <button 
            className={activeTab === 'location' ? 'active' : ''} 
            onClick={() => handleTabChange('location')}
          >
            Location
          </button>
          {matchDetails?.team_type !== 'friendly' && (
            <button 
              className={activeTab === 'head-to-head' ? 'active' : ''} 
              onClick={() => handleTabChange('head-to-head')}
            >
              Head to Head
            </button>
          )}
          {isMatchFinished() && selectedTeamType === 'academy' && (
            <button 
              className={activeTab === 'attendance' ? 'active' : ''} 
              onClick={() => handleTabChange('attendance')}
            >
              Attendees
            </button>
          )}
          {(response || isAdmin) && selectedTeamType !== 'academy' && selectedTeamType !== 'squad' && (
            <button 
              className={activeTab === 'builder' ? 'active' : ''} 
              onClick={() => handleTabChange('builder')}
            >
              Teams
            </button>
          )}
        </div>
      )}
      
      <div className="tab-content">
        {renderTabContent()}
      </div>

      {showEditModal && (
        <EditMatchModal
          match={{
            match_id: matchId,
            ...matchDetails
          }}
          onMatchUpdated={(updatedMatch) => {
            console.log('Match.js - onMatchUpdated received:', {
              opposition: updatedMatch.opposition,
              opposition_team_id: updatedMatch.opposition_team_id,
              team_type: updatedMatch.team_type
            });
            setMatchDetails(updatedMatch);
            setShowEditModal(false);
          }}
          onClose={() => setShowEditModal(false)}
        />
      )}

      {showScoresModal && (
        <ScoresModal
          match={{
            ...matchDetails,
            players: allPlayers,
            match_id: matchId,
            score_colour: matchResult?.score_colour,
            score_white: matchResult?.score_white
          }}
          onClose={() => setShowScoresModal(false)}
          onUpdate={(updatedData) => {
            // If updated data was passed from the modal, use it directly
            if (updatedData && updatedData.updatedMatch && updatedData.updatedScorers) {
              console.log('Using updated data from modal:', updatedData);
              setMatchResult(updatedData.updatedMatch);
              
              // Convert the updated scorers format to match the expected format
              const formattedScorers = {};
              Object.entries(updatedData.updatedScorers).forEach(([teamColor, scorers]) => {
                if (scorers.length > 0) {
                  formattedScorers[teamColor] = scorers;
                }
              });
              setScorers(formattedScorers);
            } else {
              // Fallback to fetching fresh data
              const fetchMatchResult = async () => {
                try {
                  const response = await api.get(`${process.env.REACT_APP_API_URL}/get_match_result.php?match_id=${matchId}`);
                  const result = response.data;
                  if (result.success) {
                    setMatchResult(result.data);
                    setScorers(result.scorers);
                  }
                } catch (error) {
                  console.error('Error fetching match result:', error);
                }
              };
              fetchMatchResult();
            }
          }}
        />
      )}

      {showSocialPostModal && (
        <SocialPostModal
          match={{
            ...matchDetails,
            ...matchResult,
            scorers: scorers,
            match_id: matchId
          }}
          onClose={() => setShowSocialPostModal(false)}
        />
      )}
    </div>
  );
}

export default Match; 